import { useCallback, useMemo } from 'react';

import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { getTwgTraceId } from './twgTraceId.ts';

/**
 * Every event in this package goes through here instead of `useAnalyticsEvents`, so the session's
 * `twgTraceId` lands on all of them without any fire site carrying it. Keep the returned
 * `createAnalyticsEvent` stable: callers pass it to `useCallback` and `useEffect` deps, and some of
 * those effects fire an event.
 */
export const useTwgAnalyticsEvents = (): {
	createAnalyticsEvent: (payload: AnalyticsEventPayload) => UIAnalyticsEvent;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const createTwgAnalyticsEvent = useCallback(
		(payload: AnalyticsEventPayload): UIAnalyticsEvent => {
			// Confluence events are `{ type, data }`. Anything else is passed straight through.
			if (!payload.data) {
				return createAnalyticsEvent(payload);
			}
			return createAnalyticsEvent({
				...payload,
				data: {
					...payload.data,
					attributes: {
						...payload.data.attributes,
						twgTraceId: getTwgTraceId(),
					},
				},
			});
		},
		[createAnalyticsEvent],
	);

	return useMemo(
		() => ({ createAnalyticsEvent: createTwgAnalyticsEvent }),
		[createTwgAnalyticsEvent],
	);
};
